/**
 * Phase 10 — Sticker Mode
 * Six poster stickers rendered on a 2D canvas. Each `render(ctx, sticker, w)`
 * draws the sticker centered at (0,0); the caller translates to the target
 * canvas position and applies scale/rotation.
 */

export const STICKER_KINDS = ["SALE", "PRICE_TAG", "STARBURST", "NEW", "OFFER", "ONLY_TODAY"];

export const PRESET_COLORS = ["#FF7A1A", "#FFC300", "#E11D48", "#0EA5E9", "#10B981", "#111827"];

const setFont = (ctx, size, weight = 900) => {
    ctx.font = `${weight} ${size}px "Poppins", "Inter", system-ui, sans-serif`;
};

const roundRect = (ctx, x, y, w, h, r) => {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
};

/** Returns the axis-aligned bounding box half-extents for a given sticker at width w. */
export function stickerHalfExtents(kind, w) {
    switch (kind) {
        case "SALE":       return { hx: w / 2, hy: w * 0.36 };
        case "PRICE_TAG":  return { hx: w / 2, hy: w * 0.52 };
        case "STARBURST": return { hx: w / 2, hy: w / 2 };
        case "NEW":        return { hx: w / 2, hy: w / 2 };
        case "OFFER":      return { hx: w / 2, hy: w * 0.28 };
        case "ONLY_TODAY": return { hx: w / 2, hy: w * 0.28 };
        default:            return { hx: w / 2, hy: w / 2 };
    }
}

function drawSale(ctx, s, w) {
    const h = w * 0.72;
    ctx.save();
    ctx.rotate(-0.05);
    ctx.fillStyle = s.color || "#FF7A1A";
    roundRect(ctx, -w / 2, -h / 2, w, h, w * 0.08);
    ctx.fill();
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = w * 0.04;
    ctx.stroke();
    ctx.fillStyle = "#ffffff";
    setFont(ctx, w * 0.32, 900);
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText((s.text || "SALE").toUpperCase(), 0, 0);
    ctx.restore();
}

function drawPriceTag(ctx, s, w) {
    const h = w * 1.04;
    const notchR = w * 0.08;
    ctx.save();
    ctx.rotate(0.06);
    ctx.fillStyle = s.color || "#111827";
    // Tag body — bottom-rounded pentagon
    ctx.beginPath();
    ctx.moveTo(-w / 2, -h / 2 + w * 0.2);
    ctx.lineTo(0, -h / 2);
    ctx.lineTo(w / 2, -h / 2 + w * 0.2);
    ctx.lineTo(w / 2, h / 2 - w * 0.12);
    ctx.quadraticCurveTo(w / 2, h / 2, w / 2 - w * 0.12, h / 2);
    ctx.lineTo(-w / 2 + w * 0.12, h / 2);
    ctx.quadraticCurveTo(-w / 2, h / 2, -w / 2, h / 2 - w * 0.12);
    ctx.closePath();
    ctx.fill();
    // Hole
    ctx.fillStyle = "#ffffff";
    ctx.beginPath();
    ctx.arc(0, -h / 2 + w * 0.18, notchR, 0, Math.PI * 2);
    ctx.fill();
    // Text
    ctx.fillStyle = "#ffffff";
    setFont(ctx, w * 0.3, 900);
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(s.text || "₹99", 0, h * 0.1);
    ctx.restore();
}

function drawStarburst(ctx, s, w) {
    const r = w / 2;
    const inner = r * 0.72;
    const points = 12;
    ctx.save();
    ctx.rotate(0.15);
    ctx.fillStyle = s.color || "#E11D48";
    ctx.beginPath();
    for (let i = 0; i < points * 2; i++) {
        const rad = (i / (points * 2)) * Math.PI * 2;
        const radius = i % 2 === 0 ? r : inner;
        const x = Math.cos(rad) * radius;
        const y = Math.sin(rad) * radius;
        if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    }
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = "#FFC300";
    ctx.lineWidth = w * 0.04;
    ctx.stroke();
    ctx.fillStyle = "#ffffff";
    setFont(ctx, w * 0.24, 900);
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(s.text || "20% OFF", 0, 0);
    ctx.restore();
}

function drawNew(ctx, s, w) {
    ctx.save();
    ctx.rotate(-0.1);
    ctx.fillStyle = s.color || "#10B981";
    roundRect(ctx, -w / 2, -w / 2, w, w, w * 0.15);
    ctx.fill();
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = w * 0.05;
    ctx.stroke();
    ctx.fillStyle = "#ffffff";
    setFont(ctx, w * 0.32, 900);
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText((s.text || "NEW").toUpperCase(), 0, 0);
    ctx.restore();
}

function drawOffer(ctx, s, w) {
    const h = w * 0.56;
    ctx.save();
    ctx.fillStyle = s.color || "#FF7A1A";
    roundRect(ctx, -w / 2, -h / 2, w, h, h / 2);
    ctx.fill();
    ctx.fillStyle = "#ffffff";
    setFont(ctx, w * 0.26, 900);
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText((s.text || "OFFER").toUpperCase(), 0, 0);
    ctx.restore();
}

function drawOnlyToday(ctx, s, w) {
    const h = w * 0.56;
    ctx.save();
    ctx.fillStyle = s.color || "#DC2626";
    ctx.fillRect(-w / 2, -h / 2, w, h);
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = w * 0.03;
    ctx.strokeRect(-w / 2, -h / 2, w, h);
    ctx.fillStyle = "#ffffff";
    setFont(ctx, w * 0.16, 900);
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText((s.text || "ONLY TODAY").toUpperCase(), 0, 0);
    ctx.restore();
}

const RENDERERS = {
    SALE: drawSale,
    PRICE_TAG: drawPriceTag,
    STARBURST: drawStarburst,
    NEW: drawNew,
    OFFER: drawOffer,
    ONLY_TODAY: drawOnlyToday,
};

/**
 * Draw a sticker layer on `ctx` at (cx, cy) with width `w` (in canvas px).
 * `sticker` = { kind, text, color, ... }.
 */
export function drawSticker(ctx, sticker, canvasW, canvasH) {
    const cx = sticker.x_percent * canvasW;
    const cy = sticker.y_percent * canvasH;
    const w = sticker.scale * canvasW;
    const renderer = RENDERERS[sticker.kind];
    if (!renderer) return;
    ctx.save();
    ctx.translate(cx, cy);
    renderer(ctx, sticker, w);
    ctx.restore();
}

/** Simple hit-test: is the point inside the sticker's rotated bounding box (approximated). */
export function stickerHit(sticker, canvasW, canvasH, px, py) {
    const cx = sticker.x_percent * canvasW;
    const cy = sticker.y_percent * canvasH;
    const w = sticker.scale * canvasW;
    const { hx, hy } = stickerHalfExtents(sticker.kind, w);
    // Ignore in-place rotation for hit testing — pads the box slightly.
    return Math.abs(px - cx) <= hx * 1.1 && Math.abs(py - cy) <= hy * 1.1;
}

/** 6 default sticker templates. `text` may be overridden by caller. */
export const STICKER_LIBRARY = [
    { kind: "SALE", label: "SALE", text: "SALE", color: "#FF7A1A" },
    { kind: "PRICE_TAG", label: "Price Tag", text: "₹99", color: "#111827" },
    { kind: "STARBURST", label: "Discount Burst", text: "20% OFF", color: "#E11D48" },
    { kind: "NEW", label: "NEW", text: "NEW", color: "#10B981" },
    { kind: "OFFER", label: "OFFER", text: "OFFER", color: "#FF7A1A" },
    { kind: "ONLY_TODAY", label: "ONLY TODAY", text: "ONLY TODAY", color: "#DC2626" },
];

/** 3 preset combinations — merchants can one-tap apply. */
export const STICKER_PRESETS = [
    {
        id: "big_sale",
        label: "Big Sale Poster",
        stickers: [
            { kind: "SALE", text: "SALE", color: "#FF7A1A", x_percent: 0.18, y_percent: 0.14, scale: 0.22 },
            { kind: "STARBURST", text: "50% OFF", color: "#E11D48", x_percent: 0.82, y_percent: 0.72, scale: 0.24 },
            { kind: "ONLY_TODAY", text: "ONLY TODAY", color: "#DC2626", x_percent: 0.5, y_percent: 0.88, scale: 0.28 },
        ],
    },
    {
        id: "new_launch",
        label: "New Launch",
        stickers: [
            { kind: "NEW", text: "NEW", color: "#10B981", x_percent: 0.18, y_percent: 0.14, scale: 0.16 },
            { kind: "OFFER", text: "OFFER", color: "#FF7A1A", x_percent: 0.5, y_percent: 0.86, scale: 0.28 },
        ],
    },
    {
        id: "price_focus",
        label: "Price Highlight",
        stickers: [
            { kind: "PRICE_TAG", text: "₹499", color: "#111827", x_percent: 0.8, y_percent: 0.24, scale: 0.24 },
            { kind: "SALE", text: "SAVE 40%", color: "#FF7A1A", x_percent: 0.5, y_percent: 0.88, scale: 0.28 },
        ],
    },
];

export const MAX_STICKERS = 5;
export const MIN_SCALE = 0.08;
export const MAX_SCALE = 0.4;
export const DEFAULT_SCALE = 0.15;
